import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Modal, Box, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import axios from "axios";
import { MdOutlineWorkspacePremium } from "react-icons/md";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { errorAlert, handleSuccess } from "../../Components/Alerts";
import CrudPageHeader from "../../Components/CrudPageHeader";
import ReusableServerDataGrid from "../../Components/ReusableServerDataGrid";
import { AddElementsButton } from "../../Components/AddElementsButton";
import { getSystemLicenseTypes } from "../../GetData/LicenseType";
import { getLicenseStatus } from "../../GetData/LicenseStatus";
import { getLicensePaymentStatus } from "../../GetData/LicensePaymentType";
import { getSystemPackage } from "../../GetData/SystemPackage";

function formatDate(value) {
  if (!value) {
    return "-";
  }

  const raw = String(value);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return raw;
  }

  const [, year, month, day] = match;
  return `${day}.${month}.${year}`;
}

function getDaysUntil(value) {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const endDate = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  const diffMs = endDate.getTime() - startOfToday.getTime();

  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

function normalizeStatusText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function getDbStatusStyle(row, statusOptions) {
  const matchedFromLookup = (statusOptions || []).find(
    (item) =>
      String(item.LicenseStatusID) === String(row.Status) ||
      normalizeStatusText(item.StatusName) === normalizeStatusText(row.StatusName)
  );

  const label = matchedFromLookup?.StatusName || row.StatusName || "Pa status";
  const normalizedLabel = normalizeStatusText(label);
  const statusId = Number(matchedFromLookup?.LicenseStatusID ?? row.Status);

  // Distinct palette for different status values from DB lookup.
  const paletteById = {
    1: { color: "#065f46", background: "#ecfdf5", border: "#6ee7b7" },
    2: { color: "#92400e", background: "#fffbeb", border: "#fcd34d" },
    3: { color: "#991b1b", background: "#fee2e2", border: "#fca5a5" },
    4: { color: "#1e3a8a", background: "#dbeafe", border: "#93c5fd" },
    5: { color: "#5b21b6", background: "#ede9fe", border: "#c4b5fd" },
  };

  if (paletteById[statusId]) {
    return {
      label,
      ...paletteById[statusId],
    };
  }

  if (normalizedLabel === "aktive" || normalizedLabel === "aktiv") {
    return {
      label,
      color: "#065f46",
      background: "#ecfdf5",
      border: "#6ee7b7",
    };
  }

  if (normalizedLabel === "skaduar") {
    return {
      label,
      color: "#991b1b",
      background: "#fee2e2",
      border: "#fca5a5",
    };
  }

  if (normalizedLabel === "e bllokuar" || normalizedLabel === "bllokuar") {
    return {
      label,
      color: "#92400e",
      background: "#fffbeb",
      border: "#fcd34d",
    };
  }

  if (normalizedLabel.includes("pezull")) {
    return {
      label,
      color: "#1e3a8a",
      background: "#dbeafe",
      border: "#93c5fd",
    };
  }

  if (normalizedLabel.includes("ne pritje") || normalizedLabel.includes("pending")) {
    return {
      label,
      color: "#5b21b6",
      background: "#ede9fe",
      border: "#c4b5fd",
    };
  }

  return {
    label,
    color: "#334155",
    background: "#f1f5f9",
    border: "#cbd5e1",
  };
}

export default function LicensesPage() {
  const headers = authHeader();
  const history = useHistory();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [quickFilter, setQuickFilter] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [licenseStatuses, setLicenseStatuses] = useState([]);
  const [licensePaymentStatuses, setLicensePaymentStatuses] = useState([]);
  const [packages, setPackages] = useState([]);
  const [editForm, setEditForm] = useState({
    LicenseID: "",
    CompanyName: "",
    IdentificationNumber: "",
    LicenseTypeID: "",
    PackageID: "",
    StartDate: "",
    EndDate: "",
    Status: "",
    PaymentStatus: "",
    Comment: "",
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(ApiLink.API_URL + "License/grid", {
        headers,
        params: {
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sort: JSON.stringify(sortModel),
          filter: JSON.stringify(filterModel.items),
          quickFilter: quickFilter.join(" "),
        },
      });

      setData(Array.isArray(response.data?.items) ? response.data.items : []);
      setRowCount(Number(response.data?.total || 0));
    } catch (error) {
      errorAlert("Gabim gjatë marrjes së licensave.");
    } finally {
      setLoading(false);
    }
  };

  const loadLookups = async () => {
    try {
      const [licenseTypeData, statusData, paymentData, packageData] = await Promise.all([
        getSystemLicenseTypes(headers),
        getLicenseStatus(headers),
        getLicensePaymentStatus(headers),
        getSystemPackage(headers),
      ]);

      setLicenseTypes(licenseTypeData || []);
      setLicenseStatuses(statusData || []);
      setLicensePaymentStatuses(paymentData || []);
      setPackages(packageData || []);
    } catch (error) {
      errorAlert("Gabim gjatë ngarkimit të lookup-eve të licensës.");
    }
  };

  useEffect(() => {
    loadLookups();
  }, []);

  useEffect(() => {
    loadData();
  }, [paginationModel, sortModel, filterModel, quickFilter]);

  const handleRelicense = (row) => {
    const source = row?.rowInfo || row || {};

    const resolvedCompanyId =
      source?.CompanyID ?? source?.companyID ?? source?.CompanyId ?? source?.companyId;

    const resolvedIdentificationNumber =
      source?.IdentificationNumber ?? source?.identificationNumber ?? "";

    const resolvedCompanyName =
      source?.CompanyName ?? source?.companyName ?? "";

    if (!resolvedCompanyId) {
      errorAlert("Nuk u gjet CompanyID për relicensim.");
      return;
    }

    const relicenseState = {
      isRelicense: true,
      companyId: resolvedCompanyId,
      identificationNumber: resolvedIdentificationNumber,
      companyName: resolvedCompanyName,
      licenseSeed: {
        LicenseTypeID: source?.LicenseTypeID ?? source?.licenseTypeID ?? null,
        PackageID: source?.PackageID ?? source?.packageID ?? null,
        Status: source?.Status ?? source?.status ?? null,
        PaymentStatus: source?.PaymentStatus ?? source?.paymentStatus ?? null,
        StartDate: source?.StartDate ?? source?.startDate ?? null,
        EndDate: source?.EndDate ?? source?.endDate ?? null,
        Comment: source?.Comment ?? source?.comment ?? "",
      },
    };

    try {
      sessionStorage.setItem("relicenseContext", JSON.stringify(relicenseState));
    } catch (error) {
      // Ignore storage failures and continue with route state/query.
    }

    const query = new URLSearchParams({
      relicense: "1",
      companyId: String(resolvedCompanyId || ""),
      identificationNumber: String(resolvedIdentificationNumber || ""),
      companyName: String(resolvedCompanyName || ""),
      licenseTypeId: String(relicenseState.licenseSeed?.LicenseTypeID ?? ""),
      packageId: String(relicenseState.licenseSeed?.PackageID ?? ""),
      statusId: String(relicenseState.licenseSeed?.Status ?? ""),
      paymentStatusId: String(relicenseState.licenseSeed?.PaymentStatus ?? ""),
      startDate: String(relicenseState.licenseSeed?.StartDate ?? ""),
      endDate: String(relicenseState.licenseSeed?.EndDate ?? ""),
      comment: String(relicenseState.licenseSeed?.Comment ?? ""),
    }).toString();

    history.push(
      {
        pathname: "/configuration/newLicense",
        search: `?${query}`,
        state: relicenseState,
      },
      relicenseState
    );
  };

  const handleEdit = (row) => {
    setEditForm({
      LicenseID: row.LicenseID || "",
      CompanyName: row.CompanyName || "",
      IdentificationNumber: row.IdentificationNumber || "",
      LicenseTypeID: row.LicenseTypeID || "",
      PackageID: row.PackageID || "",
      StartDate: row.StartDate ? String(row.StartDate).slice(0, 10) : "",
      EndDate: row.EndDate ? String(row.EndDate).slice(0, 10) : "",
      Status: row.Status || "",
      PaymentStatus: row.PaymentStatus || "",
      Comment: row.Comment || "",
    });
    setOpenEditModal(true);
  };

  const handleEditInputChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async (event) => {
    event.preventDefault();

    if (!editForm.LicenseID) {
      errorAlert("LicenseID mungon.");
      return;
    }

    try {
      setSaving(true);
      await axios.put(
        `${ApiLink.API_URL}License/update/${editForm.LicenseID}`,
        {
          LicenseTypeID: editForm.LicenseTypeID || null,
          PackageID: editForm.PackageID || null,
          StartDate: editForm.StartDate,
          EndDate: editForm.EndDate,
          Status: editForm.Status || null,
          PaymentStatus: editForm.PaymentStatus || null,
          Comment: editForm.Comment || null,
        },
        { headers }
      );

      handleSuccess("Licensa u përditësua me sukses");
      setOpenEditModal(false);
      loadData();
    } catch (error) {
      errorAlert("Gabim gjatë përditësimit të licensës.");
    } finally {
      setSaving(false);
    }
  };

  const licenseColumns = useMemo(
    () => [
      {
        field: "LicenseID",
        headerName: "#",
        minWidth: 70,
        flex: 0.45,
        filterable: false,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "CompanyName",
        headerName: "Kompania",
        minWidth: 180,
        flex: 1.3,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "IdentificationNumber",
        headerName: "Numri Identifikues",
        minWidth: 140,
        flex: 1,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "PackageName",
        headerName: "Pako",
        minWidth: 140,
        flex: 1,
        filterType: "select",
        filterOptions: packages
          .map((item) => ({ value: item.PackageName, label: item.PackageName }))
          .filter((item) => item.value),
        headerClassName: "super-app-theme--header",
      },
      {
        field: "DurationName",
        headerName: "Lloji i Licensës",
        minWidth: 150,
        flex: 1.05,
        filterType: "select",
        filterOptions: licenseTypes
          .map((item) => ({ value: item.DurationName, label: item.DurationName }))
          .filter((item) => item.value),
        headerClassName: "super-app-theme--header",
      },
      {
        field: "StartDate",
        headerName: "Fillon",
        minWidth: 110,
        flex: 0.8,
        headerClassName: "super-app-theme--header",
        renderCell: ({ row }) => formatDate(row.StartDate),
      },
      {
        field: "EndDate",
        headerName: "Përfundon",
        minWidth: 120,
        flex: 0.9,
        headerClassName: "super-app-theme--header",
        renderCell: ({ row }) => formatDate(row.EndDate),
      },
      {
        field: "StatusName",
        headerName: "Restatusi",
        minWidth: 140,
        flex: 0.9,
        filterType: "select",
        filterOptions: licenseStatuses
          .map((item) => ({ value: item.StatusName, label: item.StatusName }))
          .filter((item) => item.value),
        headerClassName: "super-app-theme--header",
        renderCell: ({ row }) => {
          const style = getDbStatusStyle(row, licenseStatuses);
          const daysUntilExpiry = getDaysUntil(row.EndDate);

          const shouldShowExpiringSoon =
            Number.isInteger(daysUntilExpiry) &&
            daysUntilExpiry >= 0 &&
            daysUntilExpiry <= 15;

          const expiringSoonText =
            daysUntilExpiry === 0
              ? " - Skadon sot"
              : ` - Skadon për ${daysUntilExpiry} ditë`;

          const badgeLabel = shouldShowExpiringSoon
            ? `${style.label}${expiringSoonText}`
            : style.label;

          const badgeColor = shouldShowExpiringSoon ? "#92400e" : style.color;
          const badgeBackground = shouldShowExpiringSoon ? "#fffbeb" : style.background;
          const badgeBorder = shouldShowExpiringSoon ? "#fcd34d" : style.border;

          return (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "11px",
                fontWeight: 700,
                color: badgeColor,
                background: badgeBackground,
                border: `1px solid ${badgeBorder}`,
                borderRadius: "999px",
                padding: "3px 10px",
                whiteSpace: "nowrap",
              }}
            >
              {badgeLabel}
            </span>
          );
        },
      },
      {
        field: "PaymentStatusName",
        headerName: "Pagesa",
        minWidth: 120,
        flex: 0.9,
        filterType: "select",
        filterOptions: licensePaymentStatuses
          .map((item) => ({ value: item.StatusName, label: item.StatusName }))
          .filter((item) => item.value),
        headerClassName: "super-app-theme--header",
      },
      {
        field: "RelicenseAction",
        headerName: "Rilicenso",
        minWidth: 125,
        flex: 0.9,
        sortable: false,
        filterable: false,
        headerClassName: "super-app-theme--header",
        renderCell: ({ row }) => (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                handleRelicense(row);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #284945",
                background: "#f5f9f7",
                color: "#284945",
                borderRadius: "8px",
                padding: "5px 10px",
                fontWeight: 700,
                lineHeight: 1.1,
                margin: 0,
                cursor: "pointer",
              }}
            >
              Rilicenso
            </button>
          </div>
        ),
      },
    ],
    [licenseStatuses, packages, licenseTypes, licensePaymentStatuses]
  );

  return (
    <div>
      <div style={{ marginBottom: "24px" }}>
        <CrudPageHeader
          icon={MdOutlineWorkspacePremium}
          title="Licensat"
          description="Menaxhoni licensat ekzistuese, bëni editim korrektues dhe hapni relicensim për kompani."
          tutorialUrl="https://www.youtube.com/@Accountree"
          tutorialLabel="Shiko Tutorialin"
          className="PITP__header"
          action={
            <AddElementsButton
              text="Gjenero Licensë"
              icon="bi bi-plus-circle"
              className="button-68"
              onClick={() => history.push("/configuration/newLicense")}
            />
          }
        />
      </div>

      <ReusableServerDataGrid
        columns={licenseColumns}
        rows={data.map((item, index) => ({
          ...item,
          id: item.LicenseID ?? `license-row-${paginationModel.page}-${index}`,
          rowInfo: item,
        }))}
        onEdit={handleEdit}
        showEditAction={true}
        showDeleteAction={false}
        showViewAction={false}
        actionsMinWidth={90}
        actionsFlex={0.7}
        height={620}
        rowCount={rowCount}
        loading={loading}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        sortModel={sortModel}
        setSortModel={setSortModel}
        filterModel={filterModel}
        setFilterModel={setFilterModel}
        quickFilter={quickFilter}
        setQuickFilter={setQuickFilter}
        getExportRows={async () => {
          const totalRowsToExport = Math.max(Number(rowCount) || 0, 1);
          const response = await axios.get(ApiLink.API_URL + "License/grid", {
            headers,
            params: {
              page: 0,
              pageSize: totalRowsToExport,
              sort: JSON.stringify(sortModel),
              filter: JSON.stringify(filterModel.items),
              quickFilter: quickFilter.join(" "),
            },
          });

          const exportItems = Array.isArray(response.data?.items) ? response.data.items : [];

          return exportItems.map((item, index) => ({
            rowNumber: index + 1,
            ...item,
            StartDate: formatDate(item.StartDate),
            EndDate: formatDate(item.EndDate),
          }));
        }}
      />

      <Modal open={openEditModal} onClose={() => setOpenEditModal(false)}>
        <Box
          sx={{
            width: { xs: "92%", sm: 680 },
            maxHeight: "88vh",
            overflowY: "auto",
            bgcolor: "background.paper",
            borderRadius: "14px",
            boxShadow: 24,
            p: 3,
            mx: "auto",
            my: "5vh",
          }}
        >
          <form onSubmit={handleSaveEdit}>
            <h3 style={{ marginTop: 0, color: "#1f3a36" }}>Edito Licensën</h3>

            <div style={{ display: "grid", gap: "12px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <TextField label="Kompania" value={editForm.CompanyName} InputProps={{ readOnly: true }} />
                <TextField label="Numri Identifikues" value={editForm.IdentificationNumber} InputProps={{ readOnly: true }} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <TextField
                  select
                  SelectProps={{ native: true }}
                  label="Lloji i Licensës"
                  name="LicenseTypeID"
                  value={editForm.LicenseTypeID}
                  onChange={handleEditInputChange}
                >
                  <option value="">Zgjedh</option>
                  {licenseTypes.map((item) => (
                    <option key={item.DurationTypeID} value={item.DurationTypeID}>{item.DurationName}</option>
                  ))}
                </TextField>

                <TextField
                  select
                  SelectProps={{ native: true }}
                  label="Pako"
                  name="PackageID"
                  value={editForm.PackageID}
                  onChange={handleEditInputChange}
                >
                  <option value="">Zgjedh</option>
                  {packages.map((item) => (
                    <option key={item.PackageID} value={item.PackageID}>{item.PackageName}</option>
                  ))}
                </TextField>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <TextField type="date" label="Data Fillimit" name="StartDate" value={editForm.StartDate} onChange={handleEditInputChange} InputLabelProps={{ shrink: true }} />
                <TextField type="date" label="Data Mbarimit" name="EndDate" value={editForm.EndDate} onChange={handleEditInputChange} InputLabelProps={{ shrink: true }} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <TextField
                  select
                  SelectProps={{ native: true }}
                  label="Statusi"
                  name="Status"
                  value={editForm.Status}
                  onChange={handleEditInputChange}
                >
                  <option value="">Zgjedh</option>
                  {licenseStatuses.map((item) => (
                    <option key={item.LicenseStatusID} value={item.LicenseStatusID}>{item.StatusName}</option>
                  ))}
                </TextField>

                <TextField
                  select
                  SelectProps={{ native: true }}
                  label="Pagesa"
                  name="PaymentStatus"
                  value={editForm.PaymentStatus}
                  onChange={handleEditInputChange}
                >
                  <option value="">Zgjedh</option>
                  {licensePaymentStatuses.map((item) => (
                    <option key={item.PaymentStatusID} value={item.PaymentStatusID}>{item.StatusName}</option>
                  ))}
                </TextField>
              </div>

              <TextField
                multiline
                rows={3}
                label="Koment"
                name="Comment"
                value={editForm.Comment}
                onChange={handleEditInputChange}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "18px" }}>
              <Button variant="outlined" onClick={() => setOpenEditModal(false)}>Anulo</Button>
              <Button type="submit" variant="contained" disabled={saving} sx={{ backgroundColor: "#284945", '&:hover': { backgroundColor: "#1f3a36" } }}>
                {saving ? "Duke ruajtur..." : "Ruaj ndryshimet"}
              </Button>
            </div>
          </form>
        </Box>
      </Modal>
    </div>
  );
}